import { flatten, sortBy } from 'lodash'
import isEqual from 'lodash/fp/isEqual'
import has from 'lodash/fp/has'
import { COMPONENT_TYPES_FILTERABLE_BY_USER_INPUT } from '@wix/wix-data-client-common'
import {
  USER_INPUT_FILTER_V1_ROLE,
  DROPDOWN_OPTIONS_ROLE,
} from '@wix/wix-data-client-common-standalone'
import { getComponentTypeData } from '../components/getComponentTypeData'

const getFilterableComponentTypes = isExperimentEnabled =>
  Object.keys(COMPONENT_TYPES_FILTERABLE_BY_USER_INPUT).filter(key => {
    const { spec } = COMPONENT_TYPES_FILTERABLE_BY_USER_INPUT[key]
    return !spec || isExperimentEnabled(spec)
  })

const getFilterableComponentsSupportedByFieldType = (
  fieldType,
  isExperimentEnabled,
) =>
  getFilterableComponentTypes(isExperimentEnabled).filter(key =>
    COMPONENT_TYPES_FILTERABLE_BY_USER_INPUT[key].supportedFieldTypes.includes(
      fieldType,
    ),
  )

const createUserInputConnection = ({
  datasetRef,
  componentRef,
  connectionConfig,
}) => ({
  componentRef,
  controllerRef: datasetRef,
  config: connectionConfig,
  role: USER_INPUT_FILTER_V1_ROLE,
})

const getUserInputFilterComponentCandidates = async ({
  editorSdkProxy,
  isExperimentEnabled,
}) => {
  const [componentsInCurrentPage, connectedComponents] = await Promise.all([
    getComponentsInCurrentPage({
      editorSdkProxy,
    }),
    getConnectedComponents({ editorSdkProxy }),
  ])

  const componentsWithTypes = await Promise.all(
    componentsInCurrentPage.map(async componentRef => {
      const { sdkType } = await getComponentTypeData({
        editorSdkProxy,
        componentRef,
      })
      return {
        componentRef,
        sdkType,
      }
    }),
  )

  const componentsFilterableByUserInput = componentsWithTypes
    .filter(({ sdkType }) => {
      const component = COMPONENT_TYPES_FILTERABLE_BY_USER_INPUT[sdkType]

      return (
        component && (!component.spec || isExperimentEnabled(component.spec))
      )
    })
    .map(({ componentRef, sdkType }) => ({
      componentRef,
      componentType: sdkType,
      disabled: connectedComponents.some(isEqual(componentRef)),
    }))

  return sortBy(componentsFilterableByUserInput, 'componentRef.id')
}

const getComponentsInCurrentPage = async ({ editorSdkProxy }) => {
  const [currentPage, allComponents] = await Promise.all([
    editorSdkProxy.pages.getCurrent(),
    editorSdkProxy.components.getAllComponents(),
  ])

  const pagesOfAllComponents = await Promise.all(
    allComponents.map(componentRef =>
      editorSdkProxy.components
        .getPage({ componentRef })
        .then(page => ({ componentRef, page })),
    ),
  )

  return pagesOfAllComponents
    .filter(({ page }) => page.id === currentPage.id)
    .map(({ componentRef }) => componentRef)
}

const getConnectedComponents = async ({ editorSdkProxy }) => {
  const controllers = await editorSdkProxy.controllers.listControllers()

  const controllerConnections = await Promise.all(
    controllers.map(({ controllerRef }) =>
      editorSdkProxy.controllers.getControllerConnections({
        controllerRef,
      }),
    ),
  ).then(flatten)

  const connectedComponents = controllerConnections
    .filter(
      ({ connection: { role } }) =>
        role !== USER_INPUT_FILTER_V1_ROLE && role !== DROPDOWN_OPTIONS_ROLE,
    )
    .map(({ componentRef }) => componentRef)

  return connectedComponents
}

const isFilterComponentConnectable = has('component.id')

export {
  getUserInputFilterComponentCandidates,
  createUserInputConnection,
  isFilterComponentConnectable,
  COMPONENT_TYPES_FILTERABLE_BY_USER_INPUT,
  getFilterableComponentsSupportedByFieldType,
}
